import { useState } from 'react';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { FormDrawer } from '@bloomays-lib/ui.shared';
import { UPSERT_BLOOMER } from '@bloomays-lib/adapter.api-bloomer';
import { internationalPhoneRegex, mailRegex, nameRegex } from '@bloomays-lib/utils.shared';

import { cancel, notify } from '../../../../helpers/toastify';
import { FieldV2, InputV2, Row2, InfoV2 } from '../formFields';
import type { DashboardBloomer } from '../../../../hooks/useMissionDashboard';

export type EditBloomerDrawerProps = {
  open: boolean;
  onClose: () => void;
  bloomer: NonNullable<DashboardBloomer>;
};

/**
 * Édition de la personne Bloomer (profil) en panneau latéral (DrawerV2) via `UPSERT_BLOOMER`.
 * Couvre prénom / nom / email / email secondaire / téléphone — mêmes règles de validation que le
 * formulaire historique `UpdateBloomerForm` (regex partagées `utils.shared`). La pièce d'identité
 * (`credentialID`, optionnelle ici) reste gérée depuis le drawer « Documents ».
 */
export const EditBloomerDrawer = ({ open, onClose, bloomer }: EditBloomerDrawerProps) => {
  const { t } = useTranslation(['missionDashboard', 'notify']);

  const NAME_ERROR = t('drawers.common.nameErrorFormat', { ns: 'missionDashboard' });
  const MAIL_ERROR = t('drawers.common.mailErrorFormat', { ns: 'missionDashboard' });
  const PHONE_ERROR = t('drawers.common.phoneErrorFormat', { ns: 'missionDashboard' });

  const [firstname, setFirstname] = useState(bloomer.firstname ?? '');
  const [lastname, setLastname] = useState(bloomer.lastname ?? '');
  const [email, setEmail] = useState(bloomer.email ?? '');
  const [secondaryEmail, setSecondaryEmail] = useState(bloomer.secondaryEmail ?? '');
  const [phoneNumber, setPhoneNumber] = useState(bloomer.phoneNumber ?? '');

  const [upsertBloomer, { loading }] = useMutation(UPSERT_BLOOMER);

  const trimmed = {
    firstname: firstname.trim(),
    lastname: lastname.trim(),
    email: email.trim(),
    secondaryEmail: secondaryEmail.trim(),
    phoneNumber: phoneNumber.trim(),
  };

  const errors = {
    firstname: trimmed.firstname.length < 1 || !nameRegex.test(trimmed.firstname) ? NAME_ERROR : undefined,
    lastname: trimmed.lastname.length < 1 || !nameRegex.test(trimmed.lastname) ? NAME_ERROR : undefined,
    email: trimmed.email.length < 1 || !mailRegex.test(trimmed.email) ? MAIL_ERROR : undefined,
    secondaryEmail: trimmed.secondaryEmail && !mailRegex.test(trimmed.secondaryEmail) ? MAIL_ERROR : undefined,
    phoneNumber: trimmed.phoneNumber && !internationalPhoneRegex.test(trimmed.phoneNumber) ? PHONE_ERROR : undefined,
  };
  const hasError = Object.values(errors).some(Boolean);

  const save = async () => {
    if (hasError) return;
    notify('info', t('updateInProgress', { ns: 'notify' }), null, { autoClose: 800, hideProgressBar: true });
    try {
      await upsertBloomer({
        variables: {
          input: {
            recordId: bloomer.recordId,
            firstname: trimmed.firstname,
            lastname: trimmed.lastname,
            email: trimmed.email,
            // Chaîne vide (et non `undefined`) quand le champ est vidé : `undefined` est omis de la
            // mutation GraphQL, laissant l'ancienne valeur en base ; `''` efface bien la cellule Airtable.
            secondaryEmail: trimmed.secondaryEmail,
            phoneNumber: trimmed.phoneNumber,
          },
        },
      });
      notify('success', t('updateSuccess', { ns: 'notify' }));
      onClose();
    } catch (e: any) {
      cancel();
      notify('error', e.message || t('randomUpdateError', { ns: 'notify' }));
    }
  };

  return (
    <FormDrawer
      open={open}
      onClose={onClose}
      title={t('drawers.editBloomer.title', { ns: 'missionDashboard' })}
      subtitle={bloomer.fullname || `${trimmed.firstname} ${trimmed.lastname}`.trim() || undefined}
      width={480}
      onSubmit={save}
      submitDisabled={hasError}
      loading={loading}
      submitIcon={<SaveRoundedIcon sx={{ fontSize: 15 }} />}
      submitAriaLabel="edit-bloomer-submit"
    >
      <Row2>
        <FieldV2 label={t('drawers.common.firstname', { ns: 'missionDashboard' })} required error={errors.firstname}>
          <InputV2
            value={firstname}
            onChange={setFirstname}
            placeholder={t('drawers.common.firstname', { ns: 'missionDashboard' })}
          />
        </FieldV2>
        <FieldV2 label={t('drawers.common.lastname', { ns: 'missionDashboard' })} required error={errors.lastname}>
          <InputV2
            value={lastname}
            onChange={setLastname}
            placeholder={t('drawers.common.lastname', { ns: 'missionDashboard' })}
          />
        </FieldV2>
      </Row2>
      <FieldV2 label={t('drawers.common.email', { ns: 'missionDashboard' })} required error={errors.email}>
        <InputV2
          type="email"
          value={email}
          onChange={setEmail}
          placeholder={t('drawers.common.emailPlaceholder', { ns: 'missionDashboard' })}
        />
      </FieldV2>
      <FieldV2
        label={t('drawers.editBloomer.secondaryEmail', { ns: 'missionDashboard' })}
        error={errors.secondaryEmail}
      >
        <InputV2
          type="email"
          value={secondaryEmail}
          onChange={setSecondaryEmail}
          placeholder={t('drawers.common.optional', { ns: 'missionDashboard' })}
        />
      </FieldV2>
      <FieldV2 label={t('drawers.common.phone', { ns: 'missionDashboard' })} error={errors.phoneNumber}>
        <InputV2
          type="tel"
          value={phoneNumber}
          onChange={setPhoneNumber}
          placeholder={t('drawers.common.optional', { ns: 'missionDashboard' })}
        />
      </FieldV2>
      <InfoV2 tone="blue" icon={<BadgeRoundedIcon sx={{ fontSize: 16 }} />}>
        {t('drawers.editBloomer.idInfo', { ns: 'missionDashboard' })}
      </InfoV2>
    </FormDrawer>
  );
};

export default EditBloomerDrawer;
